// src/components/billing/BillingConfirmDialog.jsx — the Piani confirmation (v203).
//
// Every purchase, plan change, cancellation, resume and «Annulla il cambio» is confirmed HERE before anything is
// sent — and, for a purchase, before any Stripe session is opened (opening one already retires leftover
// subscriptions). `lines` are OUR sentences (billing/confirmContent.js); `<b>…</b>` in them renders bold
// (ui/emphasis.jsx). No company-wide scope note: the page
// subtitle already says the plan is the company's. `children` hosts extra fields (the cancellation reason form).
//
// ⛔ Not admin/components/ConfirmDialog: it puts the message in one <p> and renders a thrown `e.message` (server
// English). Errors here are handled by the caller, which closes this dialog and opens an OutcomeDialog.
// ⭐ Focus starts on «Indietro»: Enter never confirms a billed action (CLAUDE.md). Escape = «Indietro» unless busy;
// a click on the overlay does nothing (an accidental dismissal would lose a typed reason). Joins modalStack, so
// Escape and the Tab trap act only while this is the top-most modal; portalled to document.body like OutcomeDialog.
// ⭐ v221 confirmDisabled: the primary button is greyed and inert (Piani's terms box not ticked yet).
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_DISPLAY, FONT_SANS, RADII } from '../../constants/theme.js';
import { useModalLayer } from '../ui/modalStack.js';
import { useContentInset } from '../ui/modalArea.js';
import { withEmphasis } from '../ui/emphasis.jsx';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled])';

// Same overlay tint and panel shadow as OutcomeDialog / AdminModal (kept literal there too).
const OVERLAY = {
  position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: FONT_SANS,
};
const PANEL = {
  background: COLORS.surface, borderRadius: RADII.control, width: '100%', maxWidth: '520px',
  maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', padding: '24px',
  boxShadow: '0 8px 40px rgba(0,0,0,0.15)', outline: 'none',
};
const TITLE = { fontFamily: FONT_DISPLAY, fontSize: '16px', fontWeight: 700, margin: '0 0 12px', color: COLORS.ink, textAlign: 'center' };
const LINE = { fontSize: '14px', lineHeight: '22px', color: COLORS.ink, margin: '0 0 10px', textAlign: 'center' };
const BUTTONS = { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px' };
const BTN = { height: '40px', padding: '0 18px', borderRadius: RADII.controlInner, fontSize: '13px', fontFamily: FONT_SANS };
const SECONDARY = { ...BTN, border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface, color: COLORS.ink };
const primary = (busy) => ({
  ...BTN, border: 'none', fontWeight: 600, color: COLORS.surface,
  background: busy ? COLORS.borderStrong : COLORS.accent, cursor: busy ? 'not-allowed' : 'pointer',
});

export default function BillingConfirmDialog({
  open, title, lines = [], confirmLabel, busy = false, confirmDisabled = false, onConfirm, onClose, children,
}) {
  const { t } = useTranslation('billing');
  const titleId = useId();
  const isTop = useModalLayer(open);
  const insetLeft = useContentInset(open);
  const panelRef = useRef(null);
  const backRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // Focus «Indietro» on open; give focus back on close (if that element is still on the page).
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    if (backRef.current) backRef.current.focus();
    return () => {
      if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (!isTop()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!busyRef.current) closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = panelRef.current.contains(active);
      if (e.shiftKey && (!inside || active === first)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (!inside || active === last)) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isTop]);

  if (!open) return null;

  return createPortal(
    <div style={insetLeft ? { ...OVERLAY, paddingLeft: `${insetLeft + 16}px` } : OVERLAY}
         data-testid="billing-confirm-overlay">
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} style={PANEL}>
        <h2 id={titleId} style={TITLE}>{title}</h2>
        {lines.map((line, i) => <p key={`${i}-${line}`} style={LINE}>{withEmphasis(line)}</p>)}
        {children}
        <div style={BUTTONS}>
          <button type="button" ref={backRef} onClick={() => closeRef.current()} disabled={busy}
                  style={{ ...SECONDARY, cursor: busy ? 'not-allowed' : 'pointer' }}>
            {t('confirm.back')}
          </button>
          <button type="button" onClick={onConfirm} disabled={busy || confirmDisabled}
                  style={primary(busy || confirmDisabled)}>
            {busy ? t('confirm.working') : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
